import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import InfinityCard from "./InfinityCard";
import InfinityRecipeEditor from "./admin/InfinityRecipeEditor";
import { MainContext } from "../App";
import {
  deserializeCraftRecipeRows,
  isFiniteCard,
  serializeCraftRecipeRows,
} from "../@shared/game/craftRecipeUtils";
import { useCardStore } from "../@shared/store/useCardsStore";

const infinityAreas = ["Health", "Defense", "Attack", "Accuracy", "Turn Loss"];
const turnsActiveOptions = [
  "Instant",
  "1 Turn",
  "2 Turns",
  "3 Turns",
  "Instant + 1 Turn",
  "2 Turns / 1 Turn",
];

const EditCardModal = ({ open, onClose, card, onUpdate }) => {
  const { setError, setMessage } = useContext(MainContext);
  const { infinityCards } = useCardStore();
  const [localCard, setLocalCard] = useState({
    name: "",
    description: "",
    effect: "",
    areaAffected: [],
    turnsActive: "",
    max: "",
    finite: false,
    craftNeeded: "",
    craftAmount: "",
    availForPurchase: false,
    price: "",
    season: "",
    imageUrl: "",
    bgColor1: "#ffffff",
    bgColor2: "#ffffff",
  });
  const [recipeRows, setRecipeRows] = useState([]);

  useEffect(() => {
    if (!card) return;

    setLocalCard({
      name: card.name || "",
      description: card.description || "",
      effect: card.effect || "",
      areaAffected: card.areaAffected || [],
      turnsActive: card.turnsActive || "",
      max: card.max || "",
      finite: isFiniteCard(card),
      craftNeeded: card.craftNeeded || "",
      craftAmount: card.craftAmount || "",
      availForPurchase: !!card.availForPurchase,
      price: card.price || "",
      season: card.season || "",
      imageUrl: card.imageUrl || "",
      bgColor1: card.bgColor1 || "#ffffff",
      bgColor2: card.bgColor2 || "#ffffff",
    });
    setRecipeRows(deserializeCraftRecipeRows(card.craftNeeded, card.craftAmount));
  }, [card]);

  const serializedRecipe = useMemo(
    () => serializeCraftRecipeRows(recipeRows),
    [recipeRows]
  );

  const cardOptions = useMemo(
    () =>
      [...(Array.isArray(infinityCards) ? infinityCards : [])]
        .map((item) => String(item?.name || "").trim())
        .filter(Boolean)
        .filter((name) => name !== localCard.name.trim())
        .sort((a, b) => a.localeCompare(b)),
    [infinityCards, localCard.name]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Card</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" height="70vh">
          <Box
            width="40%"
            bgcolor="white"
            p={2}
            display="flex"
            flexDirection="column"
            overflow="auto"
          >
            <TextField
              label="Name"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.name}
              onChange={(event) =>
                setLocalCard({ ...localCard, name: event.target.value })
              }
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.description}
              onChange={(event) =>
                setLocalCard({ ...localCard, description: event.target.value })
              }
            />
            <TextField
              label="Effect"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.effect}
              onChange={(event) =>
                setLocalCard({ ...localCard, effect: event.target.value })
              }
            />
            <FormControl fullWidth margin="dense" size="small" variant="outlined">
              <InputLabel id="edit-area-affected-label">Area Affected</InputLabel>
              <Select
                labelId="edit-area-affected-label"
                multiple
                value={localCard.areaAffected}
                onChange={(event) => {
                  const {
                    target: { value },
                  } = event;
                  setLocalCard({
                    ...localCard,
                    areaAffected:
                      typeof value === "string" ? value.split(",") : value,
                  });
                }}
                input={<OutlinedInput label="Area Affected" />}
                renderValue={(selected) => selected.join(", ")}
              >
                {infinityAreas.map((area) => (
                  <MenuItem key={area} value={area}>
                    <Checkbox checked={localCard.areaAffected.indexOf(area) > -1} />
                    <ListItemText primary={area} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense" size="small" variant="outlined">
              <InputLabel id="edit-turns-active-label">Turns Active</InputLabel>
              <Select
                labelId="edit-turns-active-label"
                value={localCard.turnsActive}
                onChange={(event) =>
                  setLocalCard({ ...localCard, turnsActive: event.target.value })
                }
                label="Turns Active"
              >
                {turnsActiveOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Max"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.max}
              onChange={(event) =>
                setLocalCard({ ...localCard, max: event.target.value })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!localCard.finite}
                  onChange={(event) =>
                    setLocalCard({ ...localCard, finite: event.target.checked })
                  }
                  size="small"
                />
              }
              label="Finite"
            />
            {localCard.finite && (
              <>
                <InfinityRecipeEditor
                  rows={recipeRows}
                  onChange={setRecipeRows}
                  cardOptions={cardOptions}
                />
                <Box
                  sx={{
                    mt: 1,
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: "rgba(0,0,0,0.04)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="caption">Stored Craft Needed</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {serializedRecipe.craftNeeded || "-"}
                  </Typography>
                  <Typography variant="caption" sx={{ mt: 1 }}>
                    Stored Craft Amount
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {serializedRecipe.craftAmount || "-"}
                  </Typography>
                </Box>
              </>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!localCard.availForPurchase}
                  onChange={(event) =>
                    setLocalCard({
                      ...localCard,
                      availForPurchase: event.target.checked,
                    })
                  }
                  size="small"
                />
              }
              label="Available for Purchase"
            />
            <TextField
              label="Price"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.price}
              onChange={(event) =>
                setLocalCard({ ...localCard, price: event.target.value })
              }
            />
            <TextField
              label="Season"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              type="number"
              value={localCard.season}
              onChange={(event) =>
                setLocalCard({ ...localCard, season: event.target.value })
              }
            />
            <TextField
              label="Image URL"
              variant="outlined"
              fullWidth
              margin="dense"
              size="small"
              value={localCard.imageUrl}
              onChange={(event) =>
                setLocalCard({ ...localCard, imageUrl: event.target.value })
              }
            />
            <Box display="flex" gap="8px" mt={1}>
              <input
                type="color"
                value={localCard.bgColor1}
                onChange={(event) =>
                  setLocalCard({ ...localCard, bgColor1: event.target.value })
                }
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
              <input
                type="color"
                value={localCard.bgColor2}
                onChange={(event) =>
                  setLocalCard({ ...localCard, bgColor2: event.target.value })
                }
                style={{
                  width: "30%",
                  height: "38px",
                  borderRadius: "5px",
                  border: "none",
                }}
              />
            </Box>
          </Box>

          <Box
            width="60%"
            bgcolor="lightgray"
            p={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="auto"
          >
            <InfinityCard
              name={localCard.name}
              description={localCard.description}
              effect={localCard.effect}
              areaAffected={localCard.areaAffected}
              turnsActive={localCard.turnsActive}
              max={localCard.max}
              finite={localCard.finite}
              craftNeeded={serializedRecipe.craftNeeded}
              craftAmount={serializedRecipe.craftAmount}
              forPurchase={localCard.availForPurchase}
              price={localCard.price}
              season={localCard.season}
              bgColor1={localCard.bgColor1}
              bgColor2={localCard.bgColor2}
              imageUrl={localCard.imageUrl}
              heightPer={80}
              borderPx={10}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            const nextRecipe = localCard.finite
              ? serializeCraftRecipeRows(recipeRows)
              : { valid: true, craftNeeded: "", craftAmount: "" };

            if (!nextRecipe.valid) {
              setError(true);
              setMessage(nextRecipe.error || "Recipe is invalid.");
              return;
            }

            if (localCard.finite && !nextRecipe.craftNeeded) {
              setError(true);
              setMessage("Finite cards need at least one recipe material.");
              return;
            }

            onUpdate({
              ...localCard,
              finite: localCard.finite ? "TRUE" : "FALSE",
              craftNeeded: localCard.finite ? nextRecipe.craftNeeded : "",
              craftAmount: localCard.finite ? nextRecipe.craftAmount : "",
            });
            onClose();
          }}
          variant="contained"
          color="primary"
          size="small"
        >
          Save
        </Button>
        <Button onClick={onClose} variant="outlined" color="secondary" size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCardModal;
